import { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Home, Lightbulb, Bookmark, User, LogIn, UserPlus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/constants';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { LCommon } from '@/components/i18n';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { AccountSheetContent } from './AccountSheetContent';
import { getSmartAuthRoute, isReturningVisitor } from '@/utils/authRedirect';

interface NavItem {
  icon: typeof Home;
  labelKey: string;
  labelDefault: string;
  path: string;
  badge?: number;
  isSheet?: boolean;
}

/**
 * Unified mobile bottom navigation
 * - Guest: Chat + Sign In (2 tabs)
 * - Logged-in: Chat, Home, Opportunities, Favorites, Account (5 tabs)
 */
export function UnifiedBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  
  // Fetch unread opportunities count for badge (on mount only)
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ['unread-opportunities-count', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const { count, error } = await supabase
        .from('opportunities')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .is('expert_viewed_at', null)
        .neq('status', 'dismissed');
      
      return error ? 0 : (count || 0);
    },
    enabled: !!user?.id,
    retry: false,
  });
  
  const isActive = (path: string) => {
    if (path === ROUTES.CHAT) {
      return location.pathname === ROUTES.CHAT || location.pathname.startsWith('/chat/');
    }
    if (path === ROUTES.ACCOUNT) {
      return location.pathname === ROUTES.ACCOUNT;
    }
    return location.pathname.startsWith(path);
  };
  
  // Memoize returning visitor check
  const isReturning = useMemo(() => isReturningVisitor(), []);
  const smartAuthRoute = useMemo(() => getSmartAuthRoute(), []);
  
  // Guest navigation items - smart auth mode detection
  const guestNavItems: NavItem[] = [
    { icon: MessageCircle, labelKey: 'nav.chat', labelDefault: 'Chat', path: ROUTES.CHAT },
    { 
      icon: isReturning ? LogIn : UserPlus, 
      labelKey: isReturning ? 'nav.sign_in' : 'nav.sign_up', 
      labelDefault: isReturning ? 'Sign In' : 'Sign Up', 
      path: smartAuthRoute 
    },
  ];
  
  // Logged-in navigation items
  const authNavItems: NavItem[] = [
    { icon: MessageCircle, labelKey: 'nav.chat', labelDefault: 'Chat', path: ROUTES.CHAT },
    { icon: Home, labelKey: 'nav.home', labelDefault: 'Home', path: ROUTES.ACCOUNT },
    { icon: Lightbulb, labelKey: 'nav.opportunities', labelDefault: 'Opps', path: ROUTES.ACCOUNT_OPPORTUNITIES, badge: unreadCount },
    { icon: Bookmark, labelKey: 'nav.favorites', labelDefault: 'Favorites', path: ROUTES.ACCOUNT_FAVORITES },
    { icon: User, labelKey: 'nav.account', labelDefault: 'Account', path: ROUTES.ACCOUNT, isSheet: true },
  ];
  
  const navItems = user ? authNavItems : guestNavItems;
  
  const handleNavClick = (item: NavItem) => {
    if (item.isSheet) {
      setAccountSheetOpen(true);
    } else {
      navigate(item.path);
    }
  };
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-t border-border md:hidden">
      <div className="flex items-center justify-around h-16 px-2 safe-area-inset-bottom">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = !item.isSheet && isActive(item.path);
          
          // Account item uses Sheet
          if (item.isSheet) {
            return (
              <Sheet key={item.labelKey} open={accountSheetOpen} onOpenChange={setAccountSheetOpen}>
                <SheetTrigger asChild>
                  <button
                    className={cn(
                      "flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors",
                      "text-muted-foreground"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-[10px] font-medium">
                      <LCommon id={item.labelKey} defaultValue={item.labelDefault} />
                    </span>
                  </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto max-h-[85vh] rounded-t-2xl">
                  <SheetHeader className="sr-only">
                    <SheetTitle><LCommon id="unified_bottom_nav.account_menu" defaultValue="Account Menu" /></SheetTitle>
                  </SheetHeader>
                  <AccountSheetContent onClose={() => setAccountSheetOpen(false)} />
                </SheetContent>
              </Sheet>
            );
          }
          
          return (
            <button
              key={item.labelKey}
              onClick={() => handleNavClick(item)}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-0.5 transition-colors relative",
                active 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <div className="relative">
                <Icon className="h-5 w-5" />
                {item.badge && item.badge > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1.5 -right-2 h-4 min-w-4 px-1 text-[10px] flex items-center justify-center"
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </Badge>
                )}
              </div>
              <span className="text-[10px] font-medium">
                <LCommon id={item.labelKey} defaultValue={item.labelDefault} />
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
